import api from './api'

export type PaymentProviderName = 'stripe' | 'nmi'

export interface BillingPlan {
  id: string
  code: string
  name: string
  description?: string
  amount_cents: number
  currency: string
  interval: 'month' | 'year'
  interval_count: number
  provider_refs: Record<string, string>
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreatePlanDto extends Record<string, unknown> {
  code: string
  name: string
  description?: string
  amount_cents: number
  currency?: string
  interval: 'month' | 'year'
  interval_count?: number
}

export type SubscriptionStatus = 'pending_checkout' | 'active' | 'past_due' | 'canceled'

export interface BillingSubscription {
  id: string
  tenant_id: string
  tenant_name?: string
  plan_id: string
  plan_name?: string
  provider: PaymentProviderName
  provider_subscription_ref?: string
  checkout_session_ref?: string
  status: SubscriptionStatus
  amount_cents: number
  currency: string
  interval: 'month' | 'year'
  interval_count: number
  current_period_end?: string
  started_at?: string
  canceled_at?: string
  created_at: string
}

export type InvoiceStatus = 'paid' | 'open' | 'failed' | 'refunded' | 'void'

export interface BillingInvoice {
  id: string
  tenant_id: string
  tenant_name?: string
  subscription_id?: string
  provider: PaymentProviderName
  provider_invoice_ref?: string
  status: InvoiceStatus
  amount_cents: number
  currency: string
  hosted_invoice_url?: string
  issued_at?: string
  paid_at?: string
}

/**
 * Discriminated payment setup: Stripe returns a hosted checkout URL to send to
 * the customer; NMI returns the Collect.js script + public tokenization key
 * for embedded card capture. Everything downstream is identical.
 */
export type SubscriptionSetup =
  | { mode: 'redirect'; url: string; session_ref: string }
  | { mode: 'embedded'; script_url: string; tokenization_key: string }

export interface CreateSubscriptionResult {
  subscription: BillingSubscription
  setup: SubscriptionSetup
}

export interface TenantBillingSummary {
  customer: { provider: PaymentProviderName; payment_method_summary?: string } | null
  active_subscription: BillingSubscription | null
  latest_invoice: BillingInvoice | null
}

class BillingService {
  async listPlans(includeInactive = false): Promise<BillingPlan[]> {
    const response = await api.get('/billing/plans', {
      params: includeInactive ? { include_inactive: true } : undefined,
    })
    return response.data
  }

  async createPlan(data: CreatePlanDto): Promise<BillingPlan> {
    const response = await api.post('/billing/plans', data)
    return response.data
  }

  async updatePlan(
    planId: string,
    data: { name?: string; description?: string; is_active?: boolean }
  ): Promise<BillingPlan> {
    const response = await api.patch(`/billing/plans/${planId}`, data)
    return response.data
  }

  async listSubscriptions(filters?: {
    status?: string
    provider?: string
    tenant_id?: string
  }): Promise<BillingSubscription[]> {
    const response = await api.get('/billing/subscriptions', { params: filters })
    return response.data
  }

  async createSubscription(
    tenantId: string,
    planId: string
  ): Promise<CreateSubscriptionResult> {
    const response = await api.post(`/billing/tenants/${tenantId}/subscriptions`, {
      plan_id: planId,
    })
    return response.data
  }

  async completeEmbeddedSubscription(
    subscriptionId: string,
    paymentToken: string
  ): Promise<BillingSubscription> {
    const response = await api.post(`/billing/subscriptions/${subscriptionId}/complete`, {
      payment_token: paymentToken,
    })
    return response.data
  }

  async cancelSubscription(subscriptionId: string): Promise<BillingSubscription> {
    const response = await api.post(`/billing/subscriptions/${subscriptionId}/cancel`)
    return response.data
  }

  async listInvoices(filters?: {
    tenant_id?: string
    status?: string
    provider?: string
  }): Promise<BillingInvoice[]> {
    const response = await api.get('/billing/invoices', { params: filters })
    return response.data
  }

  async refundInvoice(invoiceId: string, amountCents?: number): Promise<BillingInvoice> {
    const response = await api.post(`/billing/invoices/${invoiceId}/refund`, {
      amount_cents: amountCents,
    })
    return response.data
  }

  async tenantSummary(tenantId: string): Promise<TenantBillingSummary> {
    const response = await api.get(`/billing/tenants/${tenantId}/summary`)
    return response.data
  }
}

export const billingService = new BillingService()

// Helpers shared by billing pages
export function formatAmount(amountCents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100)
}

export function providerLabel(provider: PaymentProviderName): string {
  return provider === 'stripe' ? 'Stripe' : 'NMI'
}

export function monthlyAmountCents(sub: BillingSubscription): number {
  const months = sub.interval === 'year' ? 12 * sub.interval_count : sub.interval_count
  return Math.round(sub.amount_cents / Math.max(months, 1))
}
